import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuditLogFilterOptions, useListAuditLogs } from '../../features/organization/api';
import type { RecentEditedLoad } from '../../features/organization/api';
import { formatValue } from '../../lib/auditLogView';
import { RecentLoadsTab } from './RecentLoadsTab';

function actionLabel(action: string) {
  switch (action) {
    case 'CREATE':
      return 'Created';
    case 'UPDATE':
      return 'Updated';
    case 'DELETE':
      return 'Deleted';
    case 'ARCHIVE':
      return 'Archived';
    case 'UNARCHIVE':
      return 'Unarchived';
    case 'APPROVE':
      return 'Accepted';
    case 'INVITE':
      return 'Invited';
    case 'REJECT':
      return 'Rejected';
    default:
      return action;
  }
}

function actorLabel(log: any) {
  const base =
    log.actorName?.trim() ||
    log.actorEmail?.trim() ||
    (log.actorUserId ? `User ${String(log.actorUserId).slice(0, 8)}…` : 'Unknown');

  return log.actorRole ? `${base} (${log.actorRole})` : base;
}

function filterLabel(entityType: string) {
  switch (entityType) {
    case 'CUSTOMER':
      return 'Customer';
    case 'ORDER':
      return 'Load Number';
    case 'PRODUCT':
      return 'Product';
    case 'CARRIER':
      return 'Carrier';
    default:
      return 'Filter';
  }
}

function filterPlaceholder(entityType: string) {
  switch (entityType) {
    case 'CUSTOMER':
      return 'Search customer name...';
    case 'ORDER':
      return 'Search load number...';
    case 'PRODUCT':
      return 'Search product name...';
    case 'CARRIER':
      return 'Search carrier name...';
    default:
      return 'Search...';
  }
}

export function AuditLogModal({
  open,
  onClose,
  entityType,
  entityId,
  onOpenLoad,
}: {
  open: boolean;
  onClose: () => void;
  entityType: string;
  entityId?: string | null;
  /** Supplying this adds the "Recent loads" tab — only a caller that can open a
   * load has anywhere to send the click, so it gates the tab. */
  onOpenLoad?: (load: RecentEditedLoad) => void;
}) {
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const showLoadsTab = Boolean(onOpenLoad);
  const [tab, setTab] = useState<'activity' | 'loads'>('activity');

  const supportsFilter =
    !entityId && ['CUSTOMER', 'ORDER', 'PRODUCT', 'CARRIER'].includes(entityType);

  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState<any | null>(null);
  const [showOptions, setShowOptions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const filterRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      setPage(1);
      setQuery('');
      setSelectedOption(null);
      setShowOptions(false);
      setHighlightedIndex(-1);
      setTab('activity');
    }
  }, [open, entityType, entityId]);

  const optionsQuery = useAuditLogFilterOptions({
    entityType: supportsFilter ? entityType : undefined,
    q: query,
    limit: 20,
  });

  const options = optionsQuery.data?.options ?? [];
  const effectiveEntityId = entityId ?? selectedOption?.value ?? undefined;

  useEffect(() => {
    setHighlightedIndex(options.length > 0 ? 0 : -1);
  }, [query, options.length]);

  const q = useListAuditLogs({
    entityType,
    entityId: effectiveEntityId,
    page,
    pageSize,
  });

  const logs = useMemo(() => q.data?.logs ?? [], [q.data]);

  const inputValue = selectedOption ? selectedOption.label : query;

  const clearFilter = () => {
    setQuery('');
    setSelectedOption(null);
    setShowOptions(false);
    setHighlightedIndex(-1);
    setPage(1);
    inputRef.current?.focus();
  };

  const pickOption = (opt: any) => {
    setSelectedOption(opt);
    setQuery('');
    setShowOptions(false);
    setHighlightedIndex(-1);
    setPage(1);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!supportsFilter) return;

    if (selectedOption) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        clearFilter();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedOption(null);
        setShowOptions(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setShowOptions(true);
      setHighlightedIndex((prev) =>
        options.length === 0 ? -1 : Math.min(prev + 1, options.length - 1)
      );
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setShowOptions(true);
      setHighlightedIndex((prev) => (options.length === 0 ? -1 : Math.max(prev - 1, 0)));
      return;
    }

    if (e.key === 'Enter') {
      if (showOptions && highlightedIndex >= 0 && options[highlightedIndex]) {
        e.preventDefault();
        pickOption(options[highlightedIndex]);
      }
      return;
    }

    if (e.key === 'Escape') {
      setShowOptions(false);
      setHighlightedIndex(-1);
    }
  };

  const closeOptionsIfFocusLeft = () => {
    window.setTimeout(() => {
      const active = document.activeElement;
      if (filterRef.current && active && !filterRef.current.contains(active)) {
        setShowOptions(false);
        setHighlightedIndex(-1);
      }
    }, 0);
  };

  if (!open) return null;

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 900, width: '100%' }}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Audit log</h3>
            <div className="muted tiny">
              {entityType}
              {effectiveEntityId ? ` · ${effectiveEntityId}` : ''}
              {q.isFetching ? ' · Refreshing…' : ''}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--ghost" onClick={() => q.refetch()}>
              Refresh
            </button>
            <button className="btn btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {showLoadsTab && (
          // Matches the Organization tab bar. The nav keeps its own padding and
          // pill background; the wrapper supplies the modal's 16px gutter.
          <div style={{ padding: '16px 16px 0' }}>
            <nav className="settings-tabs" role="tablist" style={{ margin: 0 }}>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'activity'}
                className={`settings-tabs__item ${tab === 'activity' ? 'is-active' : ''}`}
                onClick={() => setTab('activity')}
              >
                Activity
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'loads'}
                className={`settings-tabs__item ${tab === 'loads' ? 'is-active' : ''}`}
                onClick={() => setTab('loads')}
              >
                Recent loads
              </button>
            </nav>
          </div>
        )}

        {tab === 'loads' && onOpenLoad ? (
          <div className="modal__body">
            <RecentLoadsTab active={open && tab === 'loads'} onOpenLoad={onOpenLoad} />
          </div>
        ) : (
          <div className="modal__body">
            {supportsFilter && (
              <div ref={filterRef} style={{ marginBottom: 12 }}>
                <div className="card" style={{ padding: 12 }}>
                  <div
                    className="form-control"
                    style={{ marginBottom: 0, minWidth: 0, position: 'relative' }}
                  >
                    <label>{filterLabel(entityType)}</label>

                    <input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => {
                        if (selectedOption) {
                          setSelectedOption(null);
                        }
                        setQuery(e.target.value);
                        setShowOptions(true);
                        setPage(1);
                      }}
                      onFocus={() => {
                        setShowOptions(true);
                      }}
                      onKeyDown={onInputKeyDown}
                      placeholder={filterPlaceholder(entityType)}
                      onBlur={closeOptionsIfFocusLeft}
                    />

                    {selectedOption && (
                      <div className="muted tiny" style={{ marginTop: 6 }}>
                        Selected: {selectedOption.label}
                      </div>
                    )}

                    {showOptions && !selectedOption && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 20,
                          marginTop: 4,
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: 10,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                          maxHeight: 220,
                          overflowY: 'auto',
                        }}
                      >
                        {optionsQuery.isLoading ? (
                          <div style={{ padding: 10 }} className="muted tiny">
                            Searching...
                          </div>
                        ) : options.length === 0 ? (
                          <div style={{ padding: 10 }} className="muted tiny">
                            No matches found
                          </div>
                        ) : (
                          options.map((opt: any, idx: number) => (
                            <button
                              key={opt.value}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseEnter={() => setHighlightedIndex(idx)}
                              onClick={() => pickOption(opt)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 12px',
                                border: 'none',
                                cursor: 'pointer',
                                borderBottom:
                                  idx === options.length - 1 ? 'none' : '1px solid #f3f4f6',
                                background: highlightedIndex === idx ? '#f9fafb' : 'white',
                                fontWeight: highlightedIndex === idx ? 600 : 400,
                              }}
                            >
                              {opt.label}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 10,
                      gap: 10,
                    }}
                  >
                    <div className="muted tiny">
                      {selectedOption
                        ? 'Press Backspace or Clear to change selection'
                        : 'Type to search and select'}
                    </div>

                    <button
                      className="btn btn--ghost"
                      onClick={clearFilter}
                      disabled={!query && !selectedOption}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}

            {q.isLoading ? (
              <div className="skeleton" style={{ height: 240 }} />
            ) : q.isError ? (
              <div className="banner banner--warn">Failed to load audit logs.</div>
            ) : logs.length === 0 ? (
              <div className="muted">No audit logs found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {logs.map((log: any) => (
                  <AuditLogRow key={log.id} log={log} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'activity' && (
          <div className="actions modal__footer" style={{ justifyContent: 'space-between' }}>
            <div className="muted tiny">
              Page {q.data?.page ?? page} of {q.data?.totalPages ?? 1} · {q.data?.totalCount ?? 0}{' '}
              total
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn--ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || q.isFetching}
              >
                Prev
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => setPage((p) => p + 1)}
                disabled={(q.data?.totalPages ?? 1) <= page || q.isFetching}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuditLogRow({ log }: { log: any }) {
  const [open, setOpen] = useState(false);

  const diffObj = log.diff && typeof log.diff === 'object' ? log.diff : null;
  const diffKeys = diffObj ? Object.keys(diffObj) : [];
  const hasDiff = diffKeys.length > 0;

  return (
    <div className="card" style={{ padding: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          alignItems: 'center',
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                whiteSpace: 'normal', // allow wrapping
                wordBreak: 'break-word', // wrap long words if needed
                lineHeight: 1.4,
              }}
            >
              <span
                style={{
                  border: '1px solid #e5e7eb',
                  background: '#f3f4f6',
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  marginRight: 8,
                }}
              >
                {actionLabel(log.action)}
              </span>
              {log.message || `${actionLabel(log.action)} ${log.entityType}`}
            </div>
          </div>

          <div className="muted tiny" style={{ marginTop: 4 }}>
            {new Date(log.createdAt).toLocaleString()} · {actorLabel(log)}
          </div>
        </div>

        {hasDiff && (
          <button
            className="btn btn--ghost"
            onClick={() => setOpen((v) => !v)}
            style={{
              flex: '0 0 auto',
              whiteSpace: 'nowrap',
              height: 36,
              alignSelf: 'center',
            }}
          >
            {open ? 'Hide changes' : 'View changes'}
          </button>
        )}
      </div>

      {open && hasDiff && (
        <div style={{ marginTop: 10 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>After</th>
              </tr>
            </thead>
            <tbody>
              {diffKeys.sort().map((k) => (
                <tr key={k}>
                  <td style={{ fontWeight: 700 }}>{k}</td>
                  <td>{formatValue(diffObj[k]?.before)}</td>
                  <td>{formatValue(diffObj[k]?.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
